import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from '../../libs/yup/yup';
import { updateGlobalConfig, getGlobalConfig } from '../../api/sportaloka-client';
import Swal from 'sweetalert2';

const defaultValues = {
  fee_refund_type: 'FIX',
  fee_refund_value: 0,
  fee_withdrawl_type: 'FIX',
  fee_withdrawl_value: 0,
  fee_system_type: 'FIX',
  fee_system_value: 0,
  auto_max_days: 0,
  auto_max_guests: 0
};
const schema = yup.object().shape({
  fee_refund_type: yup.string().label('Jenis Fee Refund').required('${label} wajib diisi'),
  fee_refund_value: yup
    .number()
    .label('Nilai Fee Refund')
    .required('${label} wajib diisi')
    .min(0)
    .when('fee_refund_type', {
      is: 'PERCENTAGE',
      then: (schema) => schema.max(100, 'Nilai Fee Refund tidak boleh lebih dari 100'),
      otherwise: (schema) => schema
    }),
  fee_withdrawl_type: yup.string().label('Jenis Fee Withdrawl').required('${label} wajib diisi'),
  fee_withdrawl_value: yup
    .number()
    .label('Nilai Fee Withdrawl')
    .required('${label} wajib diisi')
    .min(0)
    .when('fee_withdrawl_type', {
      is: 'PERCENTAGE',
      then: (schema) => schema.max(100, 'Nilai Fee Withdrawl tidak boleh lebih dari 100'),
      otherwise: (schema) => schema
    }),
  fee_system_type: yup.string().label('Jenis Fee Sistem').required('${label} wajib diisi'),
  fee_system_value: yup
    .number()
    .label('Nilai Fee Sistem')
    .required('${label} wajib diisi')
    .min(0)
    .when('fee_system_type', {
      is: 'PERCENTAGE',
      then: (schema) => schema.max(100, 'Nilai Fee Sistem tidak boleh lebih dari 100'),
      otherwise: (schema) => schema
    }),
  auto_max_days: yup
    .number()
    .label('Auto Approve Max Days')
    .required('${label} wajib diisi')
    .min(0),
  auto_max_guests: yup
    .number()
    .label('Auto Approve Max Guests')
    .required('${label} wajib diisi')
    .min(0)
});

const GlobalConfig = () => {
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const {
    watch,
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const watchFeeRefundType = watch('fee_refund_type', false);
  const watchFeeWithdrawlType = watch('fee_withdrawl_type', false);
  const watchFeeSystemType = watch('fee_system_type', false);

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      await updateGlobalConfig(values);
      Swal.fire({
        title: 'Berhasil!',
        text: 'Data berhasil diperbarui',
        icon: 'success',
        confirmButtonText: 'Tutup'
      });
      setLoading(false);
      getData();
    } catch (error) {
      Swal.fire({
        title: 'Gagal!',
        text: 'Periksa koneksi anda dan coba kembali.',
        icon: 'error',
        confirmButtonText: 'Tutup'
      });
      setLoading(false);
    }
  };

  const getData = async () => {
    try {
      setFetchLoading(true);
      setFetchError(false);
      const response = await getGlobalConfig();
      const responseData = response.data.data;
      reset({
        fee_refund_type: responseData.fee_refund_type,
        fee_refund_value: responseData.fee_refund_value,
        fee_withdrawl_type: responseData.fee_withdrawl_type,
        fee_withdrawl_value: responseData.fee_withdrawl_value,
        fee_system_type: responseData.fee_system_type,
        fee_system_value: responseData.fee_system_value,
        auto_max_days: responseData.auto_max_days,
        auto_max_guests: responseData.auto_max_guests
      });
      setFetchLoading(false);
    } catch (error) {
      setFetchError(true);
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);
  return (
    <div>
      <div>
        <div className="d-flex justify-content-between align-items-center">
          <h1 className="text-30 lh-14 fw-600">Global Config</h1>
        </div>
      </div>
      <div className="py-30 px-30 rounded-4 bg-white shadow-3 mt-20">
        {fetchLoading && !fetchError && <div className="text-center fw-700 my-4">Loading...</div>}
        {!fetchLoading && fetchError && (
          <div className="text-center fw-700 my-4">
            Gagal mendapatkan data{' '}
            <span className="text-danger cursor-pointer" onClick={getData}>
              Coba lagi
            </span>
          </div>
        )}
        {!fetchLoading && !fetchError && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-6">
                <div className="form-group">
                  <label>Jenis Fee Refund</label>
                  <Controller
                    control={control}
                    name="fee_refund_type"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div>
                        <select
                          disabled={loading}
                          className="form-control"
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}>
                          <option value="FIX">FIX</option>
                          <option value="PERCENTAGE">PERCENTAGE</option>
                        </select>
                        {Boolean(errors.fee_refund_type) && (
                          <span className="fw-400" style={{ color: 'red' }}>
                            {errors.fee_refund_type.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label>
                    Nilai Fee Refund {watchFeeRefundType === 'PERCENTAGE' ? '(%)' : '(IDR)'}
                  </label>
                  <Controller
                    control={control}
                    name="fee_refund_value"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div>
                        <input
                          disabled={loading}
                          className={`form-control ${Boolean(errors.fee_refund_value) && 'error'}`}
                          type="number"
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                        />
                        {Boolean(errors.fee_refund_value) && (
                          <span className="fw-400" style={{ color: 'red' }}>
                            {errors.fee_refund_value.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label>Jenis Fee Withdral</label>
                  <Controller
                    control={control}
                    name="fee_withdrawl_type"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div>
                        <select
                          disabled={loading}
                          className="form-control"
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}>
                          <option value="FIX">FIX</option>
                          <option value="PERCENTAGE">PERCENTAGE</option>
                        </select>
                        {Boolean(errors.fee_withdrawl_type) && (
                          <span className="fw-400" style={{ color: 'red' }}>
                            {errors.fee_withdrawl_type.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label>
                    Nilai Fee Withdrawl {watchFeeWithdrawlType === 'PERCENTAGE' ? '(%)' : '(IDR)'}
                  </label>
                  <Controller
                    control={control}
                    name="fee_withdrawl_value"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div>
                        <input
                          disabled={loading}
                          className={`form-control ${Boolean(errors.fee_withdrawl_value) && 'error'}`}
                          type="number"
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                        />
                        {Boolean(errors.fee_withdrawl_value) && (
                          <span className="fw-400" style={{ color: 'red' }}>
                            {errors.fee_withdrawl_value.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label>Jenis Fee System</label>
                  <Controller
                    control={control}
                    name="fee_system_type"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div>
                        <select
                          disabled={loading}
                          className="form-control"
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}>
                          <option value="FIX">FIX</option>
                          <option value="PERCENTAGE">PERCENTAGE</option>
                        </select>
                        {Boolean(errors.fee_system_type) && (
                          <span className="fw-400" style={{ color: 'red' }}>
                            {errors.fee_system_type.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label>
                    Nilai Fee System {watchFeeSystemType === 'PERCENTAGE' ? '(%)' : '(IDR)'}
                  </label>
                  <Controller
                    control={control}
                    name="fee_system_value"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div>
                        <input
                          disabled={loading}
                          className={`form-control ${Boolean(errors.fee_system_value) && 'error'}`}
                          type="number"
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                        />
                        {Boolean(errors.fee_system_value) && (
                          <span className="fw-400" style={{ color: 'red' }}>
                            {errors.fee_system_value.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label>Auto Approve Max Days</label>
                  <Controller
                    control={control}
                    name="auto_max_days"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div>
                        <input
                          disabled={loading}
                          className={`form-control ${Boolean(errors.auto_max_days) && 'error'}`}
                          type="number"
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                        />
                        {Boolean(errors.auto_max_days) && (
                          <span className="fw-400" style={{ color: 'red' }}>
                            {errors.auto_max_days.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label>Auto Approve Max Guests</label>
                  <Controller
                    control={control}
                    name="auto_max_guests"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div>
                        <input
                          disabled={loading}
                          className={`form-control ${Boolean(errors.auto_max_guests) && 'error'}`}
                          type="number"
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                        />
                        {Boolean(errors.auto_max_guests) && (
                          <span className="fw-400" style={{ color: 'red' }}>
                            {errors.auto_max_guests.message}
                          </span>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="form-group text-center mt-20">
                  <button
                    disabled={loading}
                    type="submit"
                    className="btn bg-blue-1 text-white fw-500">
                    <i className="fa fa-save mr-2" />
                    <span className="ml-10">{loading ? 'Loading...' : 'Simpan Perubahan'}</span>
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default GlobalConfig;
